import { useState, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import {
  Plus, Loader2, Upload, X, FileText, Video, Image as ImageIcon, File,
  ArrowRight, ArrowLeft, Zap, ClipboardPaste, Sparkles, BookOpen, CheckCircle2,
  GripVertical, Trash2, MessageSquareQuote, Volume2, Type, ImagePlus,
  ChevronUp, ChevronDown, Wand2,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { motion, AnimatePresence } from "framer-motion";

// --- Types & Constants ---
type CreationMethod = "pdf" | "paste" | "quick";

interface LessonBlock {
  id: string;
  type: "text" | "image" | "quiz" | "audio";
  content?: string;
  keyword?: string;
  question?: string;
  options?: string[];
  correct_answer?: string;
  emoji?: string;
}

const schema = z.object({
  title: z.string().trim().min(2, "عنوان الدرس مطلوب").max(200),
  content: z.string().trim().max(10000).optional(),
  class_id: z.string().optional(),
  scheduled_at: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

interface Props {
  classes: { id: string; name: string }[];
  onSuccess: () => void;
}

const MAX_FILE_SIZE = 200 * 1024 * 1024;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ACCEPTED_TYPES: Record<string, string> = {
  "application/pdf": "pdf",
  "application/msword": "word",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "word",
  "video/mp4": "video",
  "video/webm": "video",
  "video/quicktime": "video",
  "image/jpeg": "image",
  "image/png": "image",
  "image/webp": "image",
};

const fileTypeIcon = (type: string) => {
  switch (type) {
    case "pdf": case "word": return <FileText className="w-4 h-4" />;
    case "video": return <Video className="w-4 h-4" />;
    case "image": return <ImageIcon className="w-4 h-4" />;
    default: return <File className="w-4 h-4" />;
  }
};

const fileTypeLabel = (type: string) => {
  switch (type) {
    case "pdf": return "PDF";
    case "word": return "Word";
    case "video": return "فيديو";
    case "image": return "صورة";
    default: return "ملف";
  }
};

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const uploadWithProgress = (
  url: string,
  file: globalThis.File,
  token: string,
  onProgress: (percent: number) => void
): Promise<{ error: string | null }> =>
  new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url, true);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.setRequestHeader("x-upsert", "false");
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => resolve(xhr.status >= 200 && xhr.status < 300 ? { error: null } : { error: xhr.responseText || "Upload failed" });
    xhr.onerror = () => resolve({ error: "Network error" });
    const fd = new FormData();
    fd.append("", file, file.name);
    xhr.send(fd);
  });

const QUICK_TYPES = [
  { value: "حروف", label: "حروف", emoji: "🔤" },
  { value: "أرقام", label: "أرقام", emoji: "🔢" },
  { value: "ألوان", label: "ألوان", emoji: "🎨" },
  { value: "أشكال", label: "أشكال", emoji: "🔶" },
  { value: "نشاط", label: "نشاط", emoji: "🎮" },
  { value: "علوم", label: "علوم", emoji: "🔬" },
  { value: "إسلامي", label: "إسلامي", emoji: "🕌" },
];

const genId = () => crypto.randomUUID().slice(0, 8);

const blockTypeConfig = {
  text: { icon: Type, label: "نص", color: "bg-blue-500/10 text-blue-600 border-blue-200" },
  image: { icon: ImagePlus, label: "صورة", color: "bg-green-500/10 text-green-600 border-green-200" },
  quiz: { icon: MessageSquareQuote, label: "سؤال", color: "bg-purple-500/10 text-purple-600 border-purple-200" },
  audio: { icon: Volume2, label: "صوت", color: "bg-amber-500/10 text-amber-600 border-amber-200" },
};

// --- Step 1: Choose Method ---
function StepChooseMethod({ onSelect }: { onSelect: (m: CreationMethod) => void }) {
  const methods = [
    {
      key: "pdf" as CreationMethod,
      icon: <FileText className="w-8 h-8" />,
      title: "من ملف PDF",
      desc: "ارفع ملف PDF وسيتم تحويله لدرس تفاعلي",
      color: "from-blue-500/10 to-blue-600/10 border-blue-200 dark:border-blue-800",
      iconColor: "text-blue-600",
    },
    {
      key: "paste" as CreationMethod,
      icon: <ClipboardPaste className="w-8 h-8" />,
      title: "لصق المحتوى",
      desc: "الصق نص الدرس وسنقوم بتحويله لدرس تفاعلي",
      color: "from-emerald-500/10 to-emerald-600/10 border-emerald-200 dark:border-emerald-800",
      iconColor: "text-emerald-600",
    },
    {
      key: "quick" as CreationMethod,
      icon: <Zap className="w-8 h-8" />,
      title: "إنشاء سريع",
      desc: "أدخل العنوان والتفاصيل الأساسية فقط",
      color: "from-amber-500/10 to-amber-600/10 border-amber-200 dark:border-amber-800",
      iconColor: "text-amber-600",
    },
  ];

  return (
    <div className="space-y-4">
      <div className="text-center mb-2">
        <p className="text-muted-foreground text-sm">اختر طريقة إنشاء الدرس</p>
      </div>
      <div className="grid gap-3">
        {methods.map((m) => (
          <Card
            key={m.key}
            className={`cursor-pointer border-2 hover:shadow-md transition-all bg-gradient-to-l ${m.color}`}
            onClick={() => onSelect(m.key)}
          >
            <CardContent className="p-4 flex items-center gap-4">
              <div className={`shrink-0 ${m.iconColor}`}>{m.icon}</div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-foreground">{m.title}</p>
                <p className="text-xs text-muted-foreground">{m.desc}</p>
              </div>
              <ArrowLeft className="w-5 h-5 text-muted-foreground shrink-0" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// --- Step 2: Input Content ---
function StepInputContent({
  method,
  form,
  files, setFiles,
  coverImage, setCoverImage,
  coverPreview, setCoverPreview,
  quickType, setQuickType,
  classes,
}: {
  method: CreationMethod;
  form: ReturnType<typeof useForm<FormData>>;
  files: { file: globalThis.File; type: string }[];
  setFiles: React.Dispatch<React.SetStateAction<{ file: globalThis.File; type: string }[]>>;
  coverImage: globalThis.File | null;
  setCoverImage: React.Dispatch<React.SetStateAction<globalThis.File | null>>;
  coverPreview: string | null;
  setCoverPreview: React.Dispatch<React.SetStateAction<string | null>>;
  quickType: string;
  setQuickType: React.Dispatch<React.SetStateAction<string>>;
  classes: { id: string; name: string }[];
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { register, setValue, formState: { errors } } = form;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    const valid: { file: globalThis.File; type: string }[] = [];
    for (const f of selected) {
      if (f.size > MAX_FILE_SIZE) {
        toast({ title: "خطأ", description: `الملف "${f.name}" أكبر من 200MB`, variant: "destructive" });
        continue;
      }
      const type = ACCEPTED_TYPES[f.type];
      if (!type) {
        toast({ title: "خطأ", description: `نوع الملف "${f.name}" غير مدعوم`, variant: "destructive" });
        continue;
      }
      valid.push({ file: f, type });
    }
    setFiles((prev) => [...prev, ...valid]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleCoverSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_SIZE) {
      toast({ title: "خطأ", description: "حجم الصورة أكبر من 5MB", variant: "destructive" });
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast({ title: "خطأ", description: "الملف ليس صورة", variant: "destructive" });
      return;
    }
    setCoverImage(file);
    setCoverPreview(URL.createObjectURL(file));
    if (coverInputRef.current) coverInputRef.current.value = "";
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>عنوان الدرس *</Label>
        <Input {...register("title")} placeholder={method === "quick" ? "مثال: حرف الألف" : "عنوان الدرس"} />
        {errors.title && <p className="text-destructive text-xs mt-1">{errors.title.message}</p>}
      </div>

      {method === "quick" && (
        <div>
          <Label>نوع الدرس</Label>
          <div className="grid grid-cols-4 gap-2 mt-1.5">
            {QUICK_TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                className={`rounded-lg border-2 p-2 text-center text-xs transition-all ${
                  quickType === t.value
                    ? "border-primary bg-primary/10 text-primary font-semibold"
                    : "border-border hover:border-primary/40"
                }`}
                onClick={() => setQuickType(t.value)}
              >
                <span className="text-lg block mb-0.5">{t.emoji}</span>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {method === "pdf" ? (
        <div className="space-y-2">
          <Label>ملفات الدرس *</Label>
          <div
            className="border-2 border-dashed border-muted-foreground/25 rounded-xl p-6 text-center cursor-pointer hover:border-primary/50 transition-colors bg-muted/20"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="w-10 h-10 mx-auto text-primary/60 mb-2" />
            <p className="text-sm font-medium text-foreground">اضغط أو اسحب الملفات هنا</p>
            <p className="text-xs text-muted-foreground mt-1">PDF, Word, فيديو, صور — حتى 200MB</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.mp4,.webm,.mov,.jpg,.jpeg,.png,.webp"
            className="hidden"
            onChange={handleFileSelect}
          />
          {files.length > 0 && (
            <div className="space-y-2 mt-2">
              {files.map((f, i) => (
                <div key={i} className="flex items-center gap-2 bg-muted/50 rounded-md p-2 text-sm">
                  {fileTypeIcon(f.type)}
                  <span className="flex-1 truncate">{f.file.name}</span>
                  <Badge variant="secondary" className="text-xs">{fileTypeLabel(f.type)}</Badge>
                  <span className="text-xs text-muted-foreground">{formatSize(f.file.size)}</span>
                  <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}>
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          <div>
            <Label>وصف إضافي (اختياري)</Label>
            <Textarea {...register("content")} placeholder="أضف وصفاً أو ملاحظات..." rows={2} />
          </div>
        </div>
      ) : method === "paste" ? (
        <div>
          <Label>محتوى الدرس *</Label>
          <Textarea
            {...register("content")}
            placeholder="الصق محتوى الدرس هنا... يمكنك نسخ النص من أي مصدر"
            rows={6}
            className="min-h-[160px]"
          />
          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
            <Sparkles className="w-3 h-3" />
            سيتم تحليل المحتوى وتحويله لبلوكات تفاعلية
          </p>
        </div>
      ) : (
        <div>
          <Label>وصف الدرس</Label>
          <Textarea {...register("content")} placeholder="وصف مختصر للدرس..." rows={3} />
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>الفصل</Label>
          <Select onValueChange={(v) => setValue("class_id", v)}>
            <SelectTrigger><SelectValue placeholder="اختر الفصل" /></SelectTrigger>
            <SelectContent>
              {classes.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>التاريخ</Label>
          <Input type="datetime-local" {...register("scheduled_at")} />
        </div>
      </div>

      <div className="space-y-2">
        <Label>صورة الغلاف</Label>
        {coverPreview ? (
          <div className="relative">
            <img src={coverPreview} alt="غلاف" className="w-full h-32 object-cover rounded-lg" />
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="absolute top-2 left-2 h-7 w-7 p-0"
              onClick={() => { setCoverImage(null); setCoverPreview(null); }}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <div
            className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-3 text-center cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => coverInputRef.current?.click()}
          >
            <ImageIcon className="w-5 h-5 mx-auto text-muted-foreground mb-1" />
            <p className="text-xs text-muted-foreground">إضافة صورة غلاف (اختياري)</p>
          </div>
        )}
        <input
          ref={coverInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleCoverSelect}
        />
      </div>

      {method !== "pdf" && (
        <div className="space-y-2">
          <Label>مرفقات إضافية</Label>
          <div
            className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-3 text-center cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="w-5 h-5 mx-auto text-muted-foreground mb-1" />
            <p className="text-xs text-muted-foreground">إضافة ملفات (اختياري)</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.mp4,.webm,.mov,.jpg,.jpeg,.png,.webp"
            className="hidden"
            onChange={handleFileSelect}
          />
          {files.length > 0 && (
            <div className="space-y-1.5">
              {files.map((f, i) => (
                <div key={i} className="flex items-center gap-2 bg-muted/50 rounded-md p-2 text-sm">
                  {fileTypeIcon(f.type)}
                  <span className="flex-1 truncate text-xs">{f.file.name}</span>
                  <span className="text-xs text-muted-foreground">{formatSize(f.file.size)}</span>
                  <Button type="button" variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}>
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- Step 3: Block Builder ---
function StepBlockBuilder({
  blocks,
  setBlocks,
  converting,
  onConvert,
  onImprove,
}: {
  blocks: LessonBlock[];
  setBlocks: React.Dispatch<React.SetStateAction<LessonBlock[]>>;
  converting: boolean;
  onConvert: () => void;
  onImprove: () => void;
}) {
  const updateBlock = (id: string, updates: Partial<LessonBlock>) => {
    setBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, ...updates } : b)));
  };

  const removeBlock = (id: string) => {
    setBlocks((prev) => prev.filter((b) => b.id !== id));
  };

  const moveBlock = (id: string, direction: "up" | "down") => {
    setBlocks((prev) => {
      const idx = prev.findIndex((b) => b.id === id);
      if (idx < 0) return prev;
      const newIdx = direction === "up" ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const arr = [...prev];
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      return arr;
    });
  };

  const addBlock = (type: LessonBlock["type"]) => {
    const newBlock: LessonBlock = {
      id: genId(),
      type,
      content: type === "text" ? "" : undefined,
      keyword: type === "image" ? "" : undefined,
      question: type === "quiz" ? "" : undefined,
      options: type === "quiz" ? ["", "", ""] : undefined,
      correct_answer: type === "quiz" ? "" : undefined,
      emoji: "",
    };
    setBlocks((prev) => [...prev, newBlock]);
  };

  if (blocks.length === 0 && !converting) {
    return (
      <div className="text-center py-8 space-y-4">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
          <Wand2 className="w-8 h-8 text-primary" />
        </div>
        <div>
          <p className="font-semibold text-foreground">تحويل المحتوى لدرس تفاعلي</p>
          <p className="text-sm text-muted-foreground mt-1">
            اضغط الزر لتحويل المحتوى إلى بلوكات تعليمية تفاعلية بالذكاء الاصطناعي
          </p>
        </div>
        <Button onClick={onConvert} className="gradient-hero border-0 gap-2">
          <Sparkles className="w-4 h-4" />
          ✨ تحويل لدرس تفاعلي
        </Button>
      </div>
    );
  }

  if (converting) {
    return (
      <div className="text-center py-12 space-y-4">
        <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto" />
        <div>
          <p className="font-semibold text-foreground">جاري تحويل المحتوى...</p>
          <p className="text-sm text-muted-foreground mt-1">الذكاء الاصطناعي يحوّل المحتوى لبلوكات تفاعلية 🎮</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header with improve button */}
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-foreground">
          📦 {blocks.length} بلوك
        </p>
        <Button type="button" variant="outline" size="sm" onClick={onImprove} disabled={converting} className="gap-1 text-xs">
          <Wand2 className="w-3 h-3" />
          ✨ تحسين الدرس
        </Button>
      </div>

      {/* Block list */}
      <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
        {blocks.map((block, idx) => {
          const config = blockTypeConfig[block.type];
          const Icon = config.icon;
          return (
            <div key={block.id} className={`rounded-lg border p-3 space-y-2 ${config.color}`}>
              {/* Block header */}
              <div className="flex items-center gap-2">
                <GripVertical className="w-3.5 h-3.5 text-muted-foreground cursor-grab" />
                <Icon className="w-4 h-4" />
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                  {block.emoji || ""} {config.label}
                </Badge>
                <span className="text-[10px] text-muted-foreground">#{idx + 1}</span>
                <div className="flex-1" />
                <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => moveBlock(block.id, "up")} disabled={idx === 0}>
                  <ChevronUp className="w-3 h-3" />
                </Button>
                <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => moveBlock(block.id, "down")} disabled={idx === blocks.length - 1}>
                  <ChevronDown className="w-3 h-3" />
                </Button>
                <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive hover:text-destructive" onClick={() => removeBlock(block.id)}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>

              {/* Block content */}
              {block.type === "text" && (
                <Textarea
                  value={block.content || ""}
                  onChange={(e) => updateBlock(block.id, { content: e.target.value })}
                  placeholder="اكتب النص هنا..."
                  rows={2}
                  className="text-sm bg-background/50"
                />
              )}

              {block.type === "image" && (
                <Input
                  value={block.keyword || ""}
                  onChange={(e) => updateBlock(block.id, { keyword: e.target.value })}
                  placeholder="كلمة مفتاحية للصورة (بالإنجليزية مثل: apple, cat)"
                  className="text-sm bg-background/50"
                />
              )}

              {block.type === "audio" && (
                <Input
                  value={block.content || ""}
                  onChange={(e) => updateBlock(block.id, { content: e.target.value })}
                  placeholder="الكلمة أو الجملة للنطق..."
                  className="text-sm bg-background/50"
                />
              )}

              {block.type === "quiz" && (
                <div className="space-y-2">
                  <Input
                    value={block.question || ""}
                    onChange={(e) => updateBlock(block.id, { question: e.target.value })}
                    placeholder="نص السؤال"
                    className="text-sm bg-background/50"
                  />
                  <div className="grid grid-cols-2 gap-1.5">
                    {(block.options || []).map((opt, oi) => (
                      <Input
                        key={oi}
                        value={opt}
                        onChange={(e) => {
                          const newOpts = [...(block.options || [])];
                          newOpts[oi] = e.target.value;
                          updateBlock(block.id, { options: newOpts });
                        }}
                        placeholder={`اختيار ${oi + 1}`}
                        className={`text-xs bg-background/50 ${opt && opt === block.correct_answer ? "border-green-500 ring-1 ring-green-500/30" : ""}`}
                      />
                    ))}
                  </div>
                  <Select
                    value={block.correct_answer || ""}
                    onValueChange={(v) => updateBlock(block.id, { correct_answer: v })}
                  >
                    <SelectTrigger className="text-xs bg-background/50">
                      <SelectValue placeholder="اختر الإجابة الصحيحة" />
                    </SelectTrigger>
                    <SelectContent>
                      {(block.options || []).filter(Boolean).map((opt, i) => (
                        <SelectItem key={i} value={opt}>✅ {opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add block buttons */}
      <div className="flex gap-2 pt-1">
        <Button type="button" variant="outline" size="sm" className="flex-1 text-xs gap-1" onClick={() => addBlock("text")}>
          <Type className="w-3 h-3" /> نص
        </Button>
        <Button type="button" variant="outline" size="sm" className="flex-1 text-xs gap-1" onClick={() => addBlock("image")}>
          <ImagePlus className="w-3 h-3" /> صورة
        </Button>
        <Button type="button" variant="outline" size="sm" className="flex-1 text-xs gap-1" onClick={() => addBlock("quiz")}>
          <MessageSquareQuote className="w-3 h-3" /> سؤال
        </Button>
        <Button type="button" variant="outline" size="sm" className="flex-1 text-xs gap-1" onClick={() => addBlock("audio")}>
          <Volume2 className="w-3 h-3" /> صوت
        </Button>
      </div>
    </div>
  );
}

// --- Main Dialog ---
export default function AddLessonDialog({ classes, onSuccess }: Props) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [method, setMethod] = useState<CreationMethod>("quick");
  const [files, setFiles] = useState<{ file: globalThis.File; type: string }[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentUploadName, setCurrentUploadName] = useState("");
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [coverImage, setCoverImage] = useState<globalThis.File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [quickType, setQuickType] = useState("");
  const [blocks, setBlocks] = useState<LessonBlock[]>([]);
  const [converting, setConverting] = useState(false);
  const { profile } = useAuth();
  const { toast } = useToast();

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const resetAll = useCallback(() => {
    setStep(1);
    setMethod("quick");
    setFiles([]);
    setCoverImage(null);
    setCoverPreview(null);
    setUploadProgress(0);
    setCurrentUploadName("");
    setCurrentFileIndex(0);
    setQuickType("");
    setBlocks([]);
    setConverting(false);
    form.reset();
  }, [form]);

  const handleMethodSelect = (m: CreationMethod) => {
    setMethod(m);
    setStep(2);
  };

  const getSupabaseUploadUrl = (bucket: string, path: string) => {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    return `${supabaseUrl}/storage/v1/object/${bucket}/${path}`;
  };

  const getAccessToken = async (): Promise<string> => {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token || "";
  };

  const convertToBlocks = async (mode: "convert" | "improve" = "convert", overrideTitle?: string, overrideContent?: string) => {
    const title = overrideTitle || form.getValues("title");
    const content = overrideContent || form.getValues("content");
    let textContent = content || "";

    if (method === "quick" && quickType) {
      textContent = `[${quickType}] ${title} - ${textContent}`.trim();
    }

    if (!title && !textContent) {
      toast({ title: "خطأ", description: "أدخل عنوان أو محتوى الدرس أولاً", variant: "destructive" });
      return;
    }

    // If improving, serialize current blocks as context
    if (mode === "improve" && blocks.length > 0) {
      const blocksText = blocks.map((b) => {
        if (b.type === "text") return b.content || "";
        if (b.type === "quiz") return `سؤال: ${b.question} - خيارات: ${(b.options || []).join(", ")}`;
        if (b.type === "image") return `[صورة: ${b.keyword}]`;
        if (b.type === "audio") return `[صوت: ${b.content}]`;
        return "";
      }).join("\n");
      textContent = `الدرس الحالي:\n${blocksText}\n\nالمطلوب: تحسين وتبسيط أكثر وإضافة المزيد من الأنشطة والصور`;
    }

    setConverting(true);
    try {
      const { data, error } = await supabase.functions.invoke("convert-lesson-blocks", {
        body: { title, content: textContent, mode: mode === "improve" ? "improve" : "convert" },
      });

      if (error) {
        // Extract actual error message from FunctionsHttpError
        let errorMessage = "حدث خطأ أثناء التحويل";
        try {
          if ((error as any).context?.json) {
            const errorJson = await (error as any).context.json();
            errorMessage = errorJson?.error || errorMessage;
          }
        } catch {
          // fallback to generic message
        }
        toast({ title: "خطأ في التحويل", description: errorMessage, variant: "destructive" });
        return;
      }

      if (data?.error) {
        toast({ title: "خطأ", description: data.error, variant: "destructive" });
        return;
      }

      if (data?.blocks && Array.isArray(data.blocks)) {
        const newBlocks: LessonBlock[] = data.blocks.map((b: any) => ({
          id: genId(),
          type: b.type || "text",
          content: b.content,
          keyword: b.keyword,
          question: b.question,
          options: b.options,
          correct_answer: b.correct_answer,
          emoji: b.emoji,
        }));
        setBlocks(newBlocks);
        toast({ title: "تم التحويل بنجاح ✨", description: `تم إنشاء ${newBlocks.length} بلوك تفاعلي` });
      }
    } catch (err: any) {
      console.error("Convert error:", err);
      toast({ title: "خطأ في التحويل", description: err.message || "حاول مرة أخرى", variant: "destructive" });
    } finally {
      setConverting(false);
    }
  };

  const goToStep3 = async () => {
    const valid = await form.trigger(["title"]);
    if (!valid) return;

    const currentTitle = form.getValues("title");
    const currentContent = form.getValues("content");

    // For quick method without paste content, go to step 3 with empty blocks
    if (method === "quick") {
      setStep(3);
      // Auto-convert if we have a title
      if (currentTitle && blocks.length === 0) {
        convertToBlocks("convert", currentTitle, currentContent);
      }
      return;
    }

    // For paste method, need content
    if (method === "paste" && !currentContent) {
      toast({ title: "خطأ", description: "الصق محتوى الدرس أولاً", variant: "destructive" });
      return;
    }

    setStep(3);
    // Auto-convert
    if (blocks.length === 0) {
      convertToBlocks("convert", currentTitle, currentContent);
    }
  };

  const uploadFiles = async (lessonId: string, nurseryId: string) => {
    const uploaded: { file_name: string; file_url: string; file_type: string; file_size: number }[] = [];
    const token = await getAccessToken();
    const totalFiles = files.length;

    for (let i = 0; i < files.length; i++) {
      const { file, type } = files[i];
      setCurrentFileIndex(i + 1);
      setCurrentUploadName(file.name);
      setUploadProgress(0);

      const ext = file.name.split(".").pop() || "bin";
      const path = `${nurseryId}/${lessonId}/${crypto.randomUUID()}.${ext}`;
      const url = getSupabaseUploadUrl("lesson-files", path);

      const { error } = await uploadWithProgress(url, file, token, (percent) => {
        const baseProgress = (i / totalFiles) * 100;
        const fileContribution = percent / totalFiles;
        setUploadProgress(Math.round(baseProgress + fileContribution));
      });

      if (error) {
        console.error("Upload error:", error);
        continue;
      }
      uploaded.push({ file_name: file.name, file_url: path, file_type: type, file_size: file.size });
    }

    if (uploaded.length > 0) {
      await supabase.from("lesson_files").insert(
        uploaded.map((f) => ({ lesson_id: lessonId, nursery_id: nurseryId, ...f }))
      );
    }
    return uploaded.length;
  };

  const uploadCoverImage = async (nurseryId: string): Promise<string | null> => {
    if (!coverImage) return null;
    const ext = coverImage.name.split(".").pop() || "jpg";
    const path = `${nurseryId}/covers/${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from("lesson-files").upload(path, coverImage);
    if (error) { console.error("Cover upload error:", error); return null; }
    return path;
  };

  const onSubmit = async (data: FormData) => {
    if (!profile?.nursery_id) return;
    setUploading(true);
    setUploadProgress(0);
    setCurrentUploadName("");
    setCurrentFileIndex(0);
    try {
      const coverUrl = await uploadCoverImage(profile.nursery_id);

      // Build content from blocks or raw text
      let finalContent = data.content || null;
      if (blocks.length > 0) {
        // Combine text blocks as readable content
        const textParts = blocks
          .filter((b) => b.type === "text" && b.content)
          .map((b) => `${b.emoji || ""} ${b.content}`.trim());
        finalContent = textParts.join("\n\n") || data.content || null;
      } else if (method === "quick" && quickType) {
        finalContent = `[${quickType}] ${finalContent || ""}`.trim();
      }

      const { data: lesson, error } = await supabase.from("lessons").insert({
        title: data.title,
        content: finalContent,
        class_id: data.class_id || null,
        scheduled_at: data.scheduled_at || null,
        nursery_id: profile.nursery_id,
        media_url: coverUrl,
      }).select("id").single();

      if (error || !lesson) {
        toast({ title: "خطأ", description: error?.message || "فشل في إنشاء الدرس", variant: "destructive" });
        return;
      }

      // Save quiz blocks as lesson_activities
      const quizBlocks = blocks.filter((b) => b.type === "quiz" && b.question);
      if (quizBlocks.length > 0) {
        const activities = quizBlocks.map((b, i) => ({
          lesson_id: lesson.id,
          nursery_id: profile.nursery_id!,
          title: b.question || `سؤال ${i + 1}`,
          activity_type: "multiple_choice",
          sort_order: i,
          max_score: 1,
          content: {
            question: b.question,
            options: b.options || [],
            correct_answer: b.correct_answer || "",
          },
        }));
        await supabase.from("lesson_activities").insert(activities);
      }

      if (files.length > 0) {
        const count = await uploadFiles(lesson.id, profile.nursery_id);
        if (count < files.length) {
          toast({ title: "تنبيه", description: `تم رفع ${count} من ${files.length} ملفات`, variant: "destructive" });
        }
      }

      toast({ title: "تم إضافة الدرس بنجاح ✨" });
      resetAll();
      setOpen(false);
      onSuccess();
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setCurrentUploadName("");
    }
  };

  const stepLabels = [
    { num: 1, label: "اختر الطريقة" },
    { num: 2, label: "أدخل المحتوى" },
    { num: 3, label: "بناء الدرس" },
  ];

  // For PDF method, skip step 3 (go straight to save with files)
  const showStep3 = method !== "pdf";

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetAll(); }}>
      <DialogTrigger asChild>
        <Button className="gradient-hero border-0">
          <Plus className="w-4 h-4 ml-2" />
          إضافة درس
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-primary" />
            إضافة درس جديد
          </DialogTitle>
        </DialogHeader>

        {/* Progress Steps */}
        <div className="flex items-center gap-1 mb-2">
          {stepLabels.filter((s) => showStep3 || s.num <= 2).map((s, i, arr) => (
            <div key={s.num} className="flex items-center gap-1.5 flex-1">
              <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold shrink-0 transition-colors ${
                step >= s.num ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
              }`}>
                {step > s.num ? <CheckCircle2 className="w-4 h-4" /> : s.num}
              </div>
              <span className={`text-[11px] ${step >= s.num ? "text-foreground font-medium" : "text-muted-foreground"}`}>
                {s.label}
              </span>
              {i < arr.length - 1 && (
                <div className={`flex-1 h-0.5 rounded ${step > s.num ? "bg-primary" : "bg-muted"}`} />
              )}
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <StepChooseMethod onSelect={handleMethodSelect} />
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              {showStep3 ? (
                <div className="space-y-4">
                  <StepInputContent
                    method={method}
                    form={form}
                    files={files}
                    setFiles={setFiles}
                    coverImage={coverImage}
                    setCoverImage={setCoverImage}
                    coverPreview={coverPreview}
                    setCoverPreview={setCoverPreview}
                    quickType={quickType}
                    setQuickType={setQuickType}
                    classes={classes}
                  />
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" className="flex-1" onClick={() => setStep(1)}>
                      <ArrowRight className="w-4 h-4 ml-1" />
                      رجوع
                    </Button>
                    <Button type="button" className="flex-[2] gradient-hero border-0" onClick={goToStep3}>
                      <Sparkles className="w-4 h-4 ml-2" />
                      التالي — بناء الدرس
                    </Button>
                  </div>
                </div>
              ) : (
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <StepInputContent
                    method={method}
                    form={form}
                    files={files}
                    setFiles={setFiles}
                    coverImage={coverImage}
                    setCoverImage={setCoverImage}
                    coverPreview={coverPreview}
                    setCoverPreview={setCoverPreview}
                    quickType={quickType}
                    setQuickType={setQuickType}
                    classes={classes}
                  />

                  {uploading && files.length > 0 && (
                    <div className="space-y-2 rounded-lg border border-border bg-muted/30 p-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground truncate max-w-[200px]">
                          جاري رفع: {currentUploadName}
                        </span>
                        <span className="text-primary font-medium">
                          {currentFileIndex}/{files.length} — {uploadProgress}%
                        </span>
                      </div>
                      <Progress value={uploadProgress} className="h-2" />
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button type="button" variant="outline" className="flex-1" onClick={() => setStep(1)} disabled={uploading}>
                      <ArrowRight className="w-4 h-4 ml-1" />
                      رجوع
                    </Button>
                    <Button type="submit" className="flex-[2] gradient-hero border-0" disabled={form.formState.isSubmitting || uploading}>
                      {(form.formState.isSubmitting || uploading) ? (
                        <><Loader2 className="w-4 h-4 animate-spin ml-2" />{uploading ? "جاري الرفع..." : "جاري الحفظ..."}</>
                      ) : (
                        <><Sparkles className="w-4 h-4 ml-2" />إنشاء الدرس</>
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <StepBlockBuilder
                  blocks={blocks}
                  setBlocks={setBlocks}
                  converting={converting}
                  onConvert={() => convertToBlocks("convert")}
                  onImprove={() => convertToBlocks("improve")}
                />

                {uploading && files.length > 0 && (
                  <div className="space-y-2 rounded-lg border border-border bg-muted/30 p-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground truncate max-w-[200px]">
                        جاري رفع: {currentUploadName}
                      </span>
                      <span className="text-primary font-medium">
                        {currentFileIndex}/{files.length} — {uploadProgress}%
                      </span>
                    </div>
                    <Progress value={uploadProgress} className="h-2" />
                  </div>
                )}

                <div className="flex gap-2">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => setStep(2)} disabled={uploading || converting}>
                    <ArrowRight className="w-4 h-4 ml-1" />
                    رجوع
                  </Button>
                  <Button
                    type="submit"
                    className="flex-[2] gradient-hero border-0"
                    disabled={form.formState.isSubmitting || uploading || converting || blocks.length === 0}
                  >
                    {(form.formState.isSubmitting || uploading) ? (
                      <><Loader2 className="w-4 h-4 animate-spin ml-2" />جاري الحفظ...</>
                    ) : (
                      <><CheckCircle2 className="w-4 h-4 ml-2" />حفظ الدرس ({blocks.length} بلوك)</>
                    )}
                  </Button>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
